// @flow
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';

import type { User } from './user';

/**
 * Sentry adapter.
 *
 * @module sentry
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 */

/**
 * A Transport layer for Sentry.
 *
 * I assume there is some Transport interface specification for receiving batches of events
 * and doing something with them, usually sending them to Senry ingest.
 *
 * The most relevant Transport for us is the one provided by Sentry TestKit, which is just
 * a mock Transport with an interrogation API.
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#transport-options
 * @see https://docs.sentry.io/platforms/javascript/guides/react/configuration/sentry-testkit/
export type Transport

/**
 * Options for initializing Sentry.
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/
 */
export type InitOptions = $ReadOnly<{|
  release: string,
  /**
   * Sentry Data Source Name.
   *
   * This is a per-project unique URL that serves as an endpoint to receive events for
   * analysis by Sentry. It can be found in Project -> Settings -> SDK SETUP -> Client Keys.
   *
   * @see https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#dsn
   * @see https://docs.sentry.io/platforms/javascript/configuration/releases/
   */
  dsn?: string,
  /**
   * A number between 0 and 1, controlling the percentage chance a given transaction will be
   * sent to Sentry.
   *
   * 0 represents 0% while 1 represents 100%. Applies equally to all transactions created in
   * the app. Either this or tracesSampler must be defined to enable tracing.
   *
   * @see https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
   * @see https://docs.sentry.io/product/sentry-basics/dsn-explainer/
   */
  tracesSampleRate?: number,
  /**
   * Sets the environment.
   *
   * This string is freeform and not set by default. A release can be
   * associated with more than one environment to separate them in the UI (think staging vs
   * prod or similar).
   *
   * TODO: Come up with a scheme for this.
   *
   * @see https://docs.sentry.io/platforms/javascript/configuration/options/#environment
   * @see https://docs.sentry.io/platforms/javascript/configuration/environments/
   */
  environment?: string,
  /**
   * Controls the total amount of breadcrumbs that should be captured.
   *
   * @default 100
   * @see https://docs.sentry.io/platforms/javascript/configuration/options/#max-breadcrumbs
   * @see https://docs.sentry.io/platforms/javascript/enriching-events/breadcrumbs/
   */
  maxBreadcrumbs?: number,
  /**
   * Whether or not to send session events to Sentry.
   *
   * @see https://docs.sentry.io/platforms/javascript/configuration/options/#auto-session-tracking
   */
  autoSessionTracking?: boolean,
  /**
   * @see https://docs.sentry.io/platforms/javascript/configuration/options/#normalize-depth
   */
  normalizeDepth?: number,
  /**
   * Whether or not Sentry should dump some debug info to the console if it encounters a
   * problem processing or delivering an event.
   *
   * @see https://docs.sentry.io/platforms/javascript/configuration/options/#debug
   */
  debug?: boolean,
|}>;

export const DEFAULT_TRACES_SAMPLE_RATE: number = 1.0;

export const SCRAMBLE_COM_PROJECT_DSN: string =
  'https://ef0adf0d0a2648b5b6e45ad5e8447f60@o154638.ingest.sentry.io/1209674';

export const DEFAULT_ENVIRONMENT: string = 'scramble-com-dev';

export const DEFAULT_NORMALIZE_DEPTH: number = 10;

export const CRITICAL: 'critical' = 'critical';
export const FATAL: 'fatal' = 'fatal';
export const WARNING: 'warning' = 'warning';
export const DEBUG: 'debug' = 'debug';
export const INFO: 'info' = 'info';
export const LOG: 'log' = 'log';
export const ERROR: 'error' = 'error';

export type Level =
  | typeof CRITICAL
  | typeof FATAL
  | typeof WARNING
  | typeof DEBUG
  | typeof INFO
  | typeof LOG
  | typeof ERROR;

/**
 * Initializes Sentry in the global scope.
 *
 * Note that this has many side-effects, and should only be called once per global scope.
 */
export const initialize: (InitOptions) => void = ({
  dsn = SCRAMBLE_COM_PROJECT_DSN,
  tracesSampleRate = DEFAULT_TRACES_SAMPLE_RATE,
  environment = DEFAULT_ENVIRONMENT,
  normalizeDepth = DEFAULT_NORMALIZE_DEPTH,
  ...rest
}) => {
  Sentry.init({
    ...rest,
    integrations: [
      new Integrations.BrowserTracing({
        tracingOrigins: [window.location.hostname],
      }),
    ],

    dsn,
    tracesSampleRate,
    environment,
    normalizeDepth,
  });
};

/**
 * Capture an Error with Sentry.
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/usage/#capturing-errors
 */
export const captureException: (Error) => void = (error) =>
  Sentry.captureException(error);

/**
 * Capture a message with Sentry.
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/usage/#capturing-messages
 */
export const captureMessage: (string, level?: Level) => void = (
  message,
  level,
) => Sentry.captureMessage(message, level);

export const setUser: (User | null) => void = (user) =>
  Sentry.setUser(
    !user
      ? null
      : {
          // eslint-disable-next-line no-underscore-dangle
          id: user._id,
          username: user.username,
          email: user.email,
          // a value of '{{auto}}' here attempts to determine this automatically
          ip_address: '{{auto}}',
        },
  );
